// Сбой загрузки lazy-чанка: распознавание и единственная работающая починка.
//
// Почему тут нет «повторить import» (раньше был `lazyWithRetry`, ждавший сеть и
// звавший фабрику второй раз). Отказ загрузки модуля кэшируется в module map
// документа: повторный `import()` ТОГО ЖЕ адреса отклоняется мгновенно, без
// сетевого запроса. Проверено на проде 2026-08-31 — первый импорт 164 мс,
// второй и третий по 0 мс, ни одной новой записи в Resource Timing. Значит
// повтор фабрики не мог помочь никогда, а `React.lazy` вдобавок запоминает
// отказ навсегда: ремаунт поддерева (кнопка «Попробовать снова», сброс
// boundary) достаёт из кэша ту же ошибку.
//
// Отсюда правило: в пределах документа мёртвый чанк не оживает. Оживает только
// новый документ — поэтому починка одна, перезагрузка страницы. Она же снимает
// вторую причину: после деплоя старых файлов `/assets/*` уже нет, и свежий
// `index.html` приносит новые адреса чанков.

/** Ошибка «не смог загрузить модуль страницы» — от React.lazy или загрузчика роута. */
export function isChunkLoadError(err: unknown): boolean {
	if (!(err instanceof Error)) return false;
	const msg = err.message.toLowerCase();
	return (
		msg.includes("failed to fetch dynamically imported module") ||
		msg.includes("error loading dynamically imported module") ||
		msg.includes("loading chunk") ||
		msg.includes("loading css chunk") ||
		// Vite `preload-helper`: CSS-зависимость чанка не загрузилась — после
		// деплоя старого `/assets/*.css` уже нет, чинится тем же новым документом.
		msg.includes("unable to preload css") ||
		err.name === "ChunkLoadError"
	);
}

const RELOAD_MARK_KEY = "studyhub:chunk-reload-at";
/**
 * Окно, внутри которого автоматическая перезагрузка не повторяется. Защита от
 * цикла: если чанк недоступен не из-за сети, а по-настоящему (битый деплой,
 * прокси корпоративной сети), страница иначе перезагружалась бы бесконечно.
 */
const RELOAD_COOLDOWN_MS = 60_000;

function lastReloadAt(): number {
	try {
		return Number(window.sessionStorage.getItem(RELOAD_MARK_KEY)) || 0;
	} catch {
		// Приватный режим / отключённое хранилище — считаем, что не перезагружались.
		return 0;
	}
}

/** Можно ли перезагрузиться САМИМ (без нажатия пользователя). */
export function canAutoRecoverFromChunkLoad(): boolean {
	return Date.now() - lastReloadAt() > RELOAD_COOLDOWN_MS;
}

/**
 * Чинить ли экран сбоя чанка САМИМ, без нажатия пользователя.
 *
 * Правило одно на обе поверхности экрана (граница `React.lazy` и ошибка
 * загрузчика маршрута): офлайн перезагрузка бессмысленна — новый документ
 * взять неоткуда; онлайн она чинит ровно тот случай, который и даёт поток в
 * Hawk («Unable to preload CSS …» после деплоя, 6 человек за 5 дней): старый
 * документ просит файл `/assets/*`, которого после выката уже нет. Окно
 * остывания оставляет ручную кнопку единственным путём, если чанк мёртв
 * по-настоящему.
 */
export function shouldAutoRecoverFromChunkLoad(online: boolean): boolean {
	return online && canAutoRecoverFromChunkLoad();
}

/**
 * Перезагрузить документ, чтобы получить чистый module map и свежий index.html.
 * Ручное нажатие проходит всегда; автоматический вызов обязан сперва спросить
 * `canAutoRecoverFromChunkLoad()`.
 */
export function recoverFromChunkLoad(): void {
	try {
		window.sessionStorage.setItem(RELOAD_MARK_KEY, String(Date.now()));
	} catch {
		// Не смогли пометить — перезагружаемся всё равно, но авто-повтор
		// (`canAutoRecoverFromChunkLoad`) тогда не ограничен: ручной путь важнее.
	}
	window.location.reload();
}
